import React, { useContext } from "react";
import Avatar from "../primitives/avatar";
import * as Form from "@radix-ui/react-form";
import Link from "next/link";
import Icon from "../primitives/icon";
import {
  GameDiscussionCreateCommentModel,
  Sport,
} from "common";
import { createProfileSlug } from "@/utils/url";
import { AuthContext } from "@/app/(public)/drawer";
import { LoadingCircle } from "../primitives/loading";
import { useBettor } from "@/utils/providers/bettor";
import { useCooldown } from "@/utils/providers/cooldown";
import mlClient from "@/ml-client";
import { useAuth } from "@clerk/clerk-react";

interface CommentFormProps {
  sport: Sport;
  gameId: number;
  replyingTo?: number;
  disabled?: boolean;
  setReplying?: React.Dispatch<React.SetStateAction<boolean>>;
  setJustReplied?: React.Dispatch<React.SetStateAction<boolean>>;
}
export default function CommentForm(props: CommentFormProps) {
  const { bettor } = useBettor();
  const { getToken } = useAuth();
  const { getCooldownState, startCooldown } = useCooldown();
  const [message, setMessage] = React.useState<string>("");
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [submitted, setSubmitted] = React.useState<boolean>(false);
  const { setAuth } = useContext(AuthContext);
  const cooldownKey = `game_discussion:${props.gameId}`
  const cooldownState = getCooldownState(cooldownKey);
  const disabled = !message || submitted || props.disabled || cooldownState.isActive || bettor?.details.is_muted;
  const maxLength = 200;

  const handleSubmit = React.useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setSubmitting(true);
      setError(null);

      const comment: GameDiscussionCreateCommentModel = {
        message,
        parent_id: props.replyingTo,
      };

      console.log("Submitting comment:", comment);

      try {
        const token = await getToken();
        const baseUrl: string = `/sports/${props.sport}/games/${props.gameId}/discussion`;
        const params: URLSearchParams = new URLSearchParams();
        const request: string = `${baseUrl}?${params}`;
        const response = await mlClient.post(request, comment, token);
        if (response.ok) {
          console.log("Comment submitted:", response.data);
          setMessage("");
          setSubmitted(true);
          startCooldown(cooldownKey, 10000);
        if (props.setReplying) props.setReplying(false);
        if (props.setJustReplied) props.setJustReplied(true);
      }
      } catch (err) {
        console.error(err);
        setError("Failed to submit comment. Please try again.");
      } finally {
        setSubmitting(false);
      }
    },
    [props.replyingTo, bettor, message]
  );

  function handleMessage(event: React.ChangeEvent<HTMLInputElement>) {
    setSubmitted(false);
    setMessage(event.target.value);
  }

  if (!bettor) {
    return (
      <button
        className="w-full"
        onClick={() => {
          setAuth("register");
        }}
      >
        <div className="flex flex-row gap-2 w-full items-center pt-2">
          <Avatar />
          <input
            type="text"
            required
            placeholder={`Add a ${props.replyingTo ? "Reply" : "Comment"}`}
            className="outline-none w-full bg-surface-600 h-[44px] rounded-md text-decoration-primary text-decoration-primary px-3"
          ></input>
          <div
            className={`bg-surface-600 text-secondary-600 text-title-sm-bold flex flex-row justify-center items-center uppercase gap-1 h-[44px] min-w-[114px] rounded-md`}
          >
            {props.replyingTo ? "Reply" : "Comment"}
            <Icon
              name="arrow-right"
              size="sm"
              className={`fill-secondary-600`}
            />
          </div>
        </div>
      </button>
    );
  }

  return (
    <Form.Root
      onSubmit={handleSubmit}
      className="mt-1 flex flex-row w-full gap-2 items-center"
    >
      {bettor ? (
        <Link href={createProfileSlug(bettor.details.display_name)}>
          <Avatar img={bettor.details.avatar_url}></Avatar>
        </Link>
      ) : (
        <div className="flex gap-2 items-center justify-center">
          <div className="w-8 h-8 flex justify-center items-center bg-secondary-400 rounded-full animate-pulse"></div>
        </div>
      )}
      <Form.Field name="message" className="w-full relative group">
        <Form.Label />
        <Form.Control
          value={message}
          onChange={handleMessage}
          type="text"
          required
          placeholder={
            cooldownState.isActive
              ? `10s cooldown...`
              : bettor?.details.is_muted ? `You are muted and can't comment.`
              : `Add a ${props.replyingTo ? "Reply" : "Comment"}`
          }
          maxLength={maxLength}
          className="outline-none w-full bg-surface-600 h-[44px] rounded-md text-decoration-primary text-decoration-primary pl-3 pr-14"
        />
        <div className="absolute h-full right-0 top-0 bottom-0 pr-3 text-secondary-500 text-label-medium pt-0.5 flex items-center">
          {message.length}/{maxLength}
        </div>
      </Form.Field>
      <Form.Submit
        disabled={disabled}
        className="btn btn-md btn-filled-primary md:min-w-[114px]"
      >
        {cooldownState.isActive && cooldownState.countdown > 0 ? (
          <div className="flex items-center gap-2 justify-center w-full">
            <span className="block">{cooldownState.countdown}</span>
            <Icon
              name="clock"
              size="sm"
              className="fill-secondary-600 hidden md:block"
            />
          </div>
        ) : submitted ? (
          <>
            <span className="hidden md:block">
              {props.replyingTo ? "Replied" : "Commented"}
            </span>
            <Icon name="checkmark" size="sm" className="fill-secondary-600" />
          </>
        ) : submitting ? (
          <>
            <span className="hidden md:block">
              {props.replyingTo ? "Reply" : "Comment"}
            </span>
            <LoadingCircle size={16} stroke={10} />
          </>
        ) : (
          <>
            <span className="hidden md:block">
              {props.replyingTo ? "Reply" : "Comment"}
            </span>
            <Icon
              name="arrow-right"
              size="sm"
              className={`${
                !message || disabled
                  ? "fill-secondary-600"
                  : "fill-secondary-300"
              }`}
            />
          </>
        )}
      </Form.Submit>
    </Form.Root>
  );
}
